<template>
  <v-dialog
    v-model="dialog"
    persistent
    transition="dialog-bottom-transition"
    max-width="1000"
    v-if="isDataLoadedFromServer"
  >
    <div class="bg-white poppins">
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">
          {{ $t("add") }} {{ $t("asn") }}
        </h4>
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
      </div>
      <div class="modal-body d-flex flex-column scroll-y mx-5 py-7">
        <v-stepper
          v-model="tab"
          non-linear
          outlined
          rounded="4"
          flat
          v-if="isStepperReady"
          @change="updateValidationStateOfSteps"
        >
          <v-stepper-header class="step-head-main">
            <v-stepper-step
              v-for="(item, i) in categories"
              :key="i"
              class="step-head"
              complete
              editable
              complete-icon="mdi-check-circle"
              edit-icon="mdi-check"
              error-icon="mdi-alert-circle"
              :step="i"
              :rules="[
                () => {
                  return item.valid;
                },
              ]"
            >
              {{ $t(item.nameKey) }}
              <small v-if="!item.valid">{{ $t("invalid") }}</small>
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items class="">
            <v-stepper-content
              step="0"
              key="Pre-Alert Information"
              class="pt-2"
            >
              <div class="row mt-2">
                <div class="col-12 col-sm-6 h-75px" v-if="!isClient">
                  <v-select
                    v-model="pre_alert.customer_id"
                    :label="$t('customer')"
                    item-text="text"
                    item-value="index"
                    :items="serverData.clients"
                    clearable
                    outlined
                    dense
                    :error-messages="customer_idErrors"
                    @blur="$v.pre_alert.customer_id.$touch()"
                  />
                </div>

                <div
                  class="col-12 h-75px"
                  :class="[isClient ? 'col-sm-12' : 'col-sm-6']"
                >
                  <v-autocomplete
                    v-model="pre_alert.warehouse_id"
                    :label="$t('warehouse')"
                    item-text="text"
                    item-value="index"
                    :items="serverData.warehouses"
                    clearable
                    outlined
                    dense
                    :error-messages="warehouse_idErrors"
                    @blur="$v.pre_alert.warehouse_id.$touch()"
                  />
                </div>
                <div class="col-12 col-sm-6 h-75px">
                  <v-select
                    v-model="asn_info.asn_type_id"
                    :label="$t('asn_type')"
                    item-text="text"
                    item-value="index"
                    :items="serverData.asn_types"
                    clearable
                    outlined
                    dense
                    :error-messages="asn_type_idErrors"
                    @blur="$v.asn_info.asn_type_id.$touch()"
                  />
                </div>
                <div class="col-12 col-sm-6 h-75px">
                  <v-text-field
                    v-model="pre_alert.reference_number"
                    :label="$t('reference_number')"
                    clearable
                    outlined
                    dense
                  />
                </div>
                <div class="col-12 col-sm-6 h-75px">
                  <v-text-field
                    v-model="pre_alert.carrier"
                    :label="$t('carrier')"
                    clearable
                    outlined
                    dense
                  />
                </div>
                <div class="col-12 col-sm-6 h-75px">
                  <v-text-field
                    v-model="pre_alert.supplier_reference_code"
                    :label="$t('supplier_reference_code')"
                    clearable
                    outlined
                    dense
                  />
                </div>

                <div
                  v-if="asn_info.asn_type_id !== 1"
                  class="col-12 col-sm-6 h-75px"
                >
                  <v-datetime-picker
                    :label="$t('expected_arrival_time')"
                    :textFieldProps="{
                      outlined: true,
                      dense: true,
                      'prepend-icon': 'mdi-calendar',
                    }"
                    :datePickerProps="{
                      min: today,
                    }"
                    :timePickerProps="{}"
                    v-model="pre_alert.expected_arrival_time"
                  >
                  </v-datetime-picker>
                </div>
                <div v-else class="col-12 col-sm-6 h-75px">
                  <DateSlotPicker
                    :pageLoader="pageLoader"
                    :pre_alert="pre_alert"
                    :slotErrors="slotErrors"
                  />
                  <!--                  @slot-selected="handleSlotSelect"-->
                </div>
                <div class="col-12 col-sm-6 h-75px">
                  <v-datetime-picker
                    :label="$t('actual_arrival_time')"
                    :textFieldProps="{
                      outlined: true,
                      dense: true,
                      'prepend-icon': 'mdi-calendar',
                    }"
                    :datePickerProps="{}"
                    :timePickerProps="{}"
                    v-model="pre_alert.actual_arrival_time"
                  >
                  </v-datetime-picker>
                </div>
                <div class="col-12 col-sm-6 col-md-4 h-75px">
                  <v-text-field
                    v-model="pre_alert.po_number"
                    :label="$t('purchase_order_number')"
                    clearable
                    outlined
                    dense
                  />
                </div>
                <div class="col-12 col-sm-6 col-md-4 h-75px">
                  <v-text-field
                    v-model="pre_alert.vehicle_number"
                    :label="$t('vehicle_number')"
                    clearable
                    outlined
                    dense
                  />
                </div>
                <div class="col-12 col-sm-6 col-md-4 h-75px">
                  <v-autocomplete
                    v-model="pre_alert.vehicle_type_id"
                    :label="$t('vehicle_type')"
                    clearable
                    outlined
                    dense
                    item-text="text"
                    item-value="index"
                    :items="serverData.vehicle_types"
                  />
                </div>
                <div class="col-12 col-sm-6 col-md-4 h-75px">
                  <v-text-field
                    v-model="pre_alert.vehicle_temperature"
                    :label="$t('vehicle_temperature')"
                    clearable
                    outlined
                    dense
                  />
                </div>
                <div class="col-12 col-sm-6 col-md-4 h-75px">
                  <v-text-field
                    v-model="pre_alert.invoice_number"
                    :label="$t('invoice_number')"
                    clearable
                    outlined
                    dense
                  />
                </div>
                <div class="col-12 col-sm-6 col-md-4 h-75px">
                  <v-text-field
                    v-model="pre_alert.number_of_boxes"
                    :label="$t('number_of_boxes')"
                    clearable
                    outlined
                    dense
                    type="number"
                    @change="
                      () => validateMinValue('number_of_boxes', 0, 'pre_alert')
                    "
                  />
                </div>
                <div class="col-12 h-75px">
                  <v-text-field
                    v-model="pre_alert.note"
                    :label="$t('comment')"
                    clearable
                    outlined
                    dense
                  />
                </div>
              </div>
            </v-stepper-content>
            <v-stepper-content step="1" key="ASN Information" class="pt-2">
              <div class="row mt-2">
                <div
                  class="col-12 col-sm-6 h-75px d-flex align-start justify-space-between ma-0 pa-0 px-3"
                >
                  <h4 class="pr-4 pt-5 font-weight-regular">
                    {{ $t("put_on_hold") }}
                  </h4>
                  <v-switch
                    v-model="asn_info.release_status"
                    hide-details
                    dense
                    inset
                  />
                </div>
                <div
                  class="col-12 ma-0 pa-0 row"
                  v-if="asn_info.asn_type_id === 5"
                >
                  <div class="col-12 col-sm-6 h-75px">
                    <v-text-field
                      v-model="asn_info.original_order_awb"
                      :label="$t('original_order_awb')"
                      clearable
                      outlined
                      dense
                      :error-messages="original_order_awbErrors"
                      @blur="$v.asn_info.original_order_awb.$touch()"
                    />
                  </div>
                  <div class="col-12 col-sm-6 h-75px">
                    <v-text-field
                      v-model="asn_info.original_order_no"
                      :label="$t('original_order_number')"
                      clearable
                      outlined
                      dense
                      :error-messages="original_order_noErrors"
                      @blur="$v.asn_info.original_order_no.$touch()"
                    />
                  </div>
                  <div class="col-12 col-sm-6 h-75px">
                    <v-text-field
                      v-model="asn_info.return_order_awb"
                      :label="$t('return_order_awb')"
                      clearable
                      outlined
                      dense
                      :error-messages="return_order_awbErrors"
                      @blur="$v.asn_info.return_order_awb.$touch()"
                    />
                  </div>
                  <div
                    class="col-12 col-sm-6 h-75px d-flex align-start justify-space-between ma-0 pa-0 px-3"
                  >
                    <h4 class="pr-4 pt-5 font-weight-regular">
                      {{ $t("replacement") }}
                    </h4>
                    <v-switch
                      v-model="asn_info.replacement_indicator"
                      :true-value="1"
                      :false-value="0"
                      hide-details
                      dense
                      inset
                    />
                  </div>
                </div>
                <div class="col-12">
                  <v-radio-group
                    :label="$t('receiving_type')"
                    v-model="asn_info.receiving_type"
                    row
                  >
                    <v-radio
                      v-for="(desti, i) in receivingTypes"
                      :label="desti.text"
                      :value="desti.index"
                      :key="i"
                    />
                  </v-radio-group>
                </div>
                <div class="col-12">
                  <sku-datatable
                    :setSku="setSkus"
                    :skus="formData.skus"
                    :skuItem="serverData.skus"
                    :carriers="serverData.carriers"
                    :clientId="userId"
                    :customer="getCustomerData"
                    :receiving_type="asn_info.receiving_type"
                  />
                </div>
              </div>
            </v-stepper-content>
            <v-stepper-content step="2" key="Quantity Information" class="pt-2">
              <div class="row pt-4">
                <div class="col-12">
                  <div class="col-sm-6 col-md-6 col-lg-6 col-12">
                    <v-text-field
                      v-model="qty_info.number_of_pallets_used"
                      :label="$t('number_of_pallets_used')"
                      clearable
                      outlined
                      dense
                      type="Number"
                      min="0"
                      @change="
                        () =>
                          validateMinValue(
                            'number_of_pallets_used',
                            0,
                            'qty_info'
                          )
                      "
                      :error-messages="number_of_pallets_usedErrors"
                      @blur="$v.qty_info.number_of_pallets_used.$touch()"
                    />
                  </div>
                  <div class="col-12">
                    <qty-datatable
                      :setSku="setQtys"
                      :skus="formData.additional_services_quantities"
                      :qty_types="serverData.qty_types"
                    />
                  </div>
                </div>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

        <!--begin::Body-->

        <!--end::Body-->
        <div class="d-flex align-center modal-action flex-wrap pt-4">
          <div class="justify-content-between align-center d-flex col-12">
            <v-btn
              elevation="0"
              color="primary"
              dark
              class="order-1 order-sm-1"
              :class="{ invisible: !backState }"
              @click="() => nextStep('prev')"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <div
              class="order-2 order-sm-2 d-flex flex-wrap justify-content-center align-center"
              :class="{ invisible: tab !== 2 }"
            >
              <button
                type="submit"
                class="btn btn-danger px-5 py-3 mx-2 order-2 mt-1 mt-sm-0"
                @click="resetCreateForm"
              >
                {{ $t("clear") }}
              </button>
              <button
                type="submit"
                class="btn btn-info px-5 py-3 mx-2 order-1 mt-1 mt-sm-0"
                @click="submitCreateForm"
              >
                {{ $t("submit") }}
              </button>
            </div>
            <v-btn
              elevation="0"
              class="order-3 order-sm-3"
              color="primary"
              dark
              :class="{ invisible: !nextState }"
              @click="() => nextStep('next')"
            >
              <v-icon>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import Swal from "sweetalert2";
import SkuDatatable from "@/own/components/fulfillment/asn/skuContainer/SkuDatatable";
import ApiService from "@/core/services/api.service";
import swalAdded from "@/core/services/AlertServices/SwalAdded";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";
import QtyDatatable from "@/own/components/fulfillment/asn/qtyContainer/QtyDatatable";
import DateSlotPicker from "@/own/components/fulfillment/asn/DateSlotPicker.vue";

export default {
  mixins: [validationMixin, fieldValueValidation],
  validations() {
    return {
      pre_alert: {
        warehouse_id: { required },
        slot: {
          required: requiredIf(function () {
            return this.asn_info.asn_type_id === 1;
          }),
        },
        customer_id: {
          required: requiredIf(function () {
            return !this.isClient;
          }),
        },
      },
      qty_info: {
        number_of_pallets_used: { required },
      },
      asn_info: {
        asn_type_id: { required },
        original_order_awb: {
          required: requiredIf(function () {
            return this.asn_info.asn_type_id === 5;
          }),
        },
        original_order_no: {
          required: requiredIf(function () {
            return this.asn_info.asn_type_id === 5;
          }),
        },
        return_order_awb: {
          required: requiredIf(function () {
            return this.asn_info.asn_type_id === 5;
          }),
        },
      },
    };
  },
  components: { DateSlotPicker, SkuDatatable, QtyDatatable },
  name: "AddItem",
  props: ["refresher", "pageLoader"],
  data: () => ({
    updateFlag: false,
    dialog: false,
    dateModal: false,
    serverData: null,
    menu2: false,
    formData: {
      skus: [],
      additional_services_quantities: [],
    },
    pre_alert: {
      customer_id: null,
      warehouse_id: null,
      carrier: null,
      supplier_reference_code: null,
      expected_arrival_time: null,
      slot: "",
      po_number: null, // Purchase Order Number
      vehicle_number: null,
      vehicle_type_id: null,
      vehicle_temperature: null,
      invoice_number: null,
      number_of_boxes: null,
      actual_arrival_time: null,
      note: null,
      reference_number: null,
    },
    today: new Date().toISOString().split("T")[0],
    asn_info: {
      asn_type_id: null,
      receiving_type: 1,
      release_status: false, // put on hold
      replacement_indicator: 0,
      original_order_awb: null,
      original_order_no: null,
      return_order_awb: null,
    },
    qty_info: {
      number_of_pallets_used: 0,
    },
    tab: 0,
    categories: [
      {
        nameKey: "pre_alert_information",
        valid: true,
        rules: (el) => {
          try {
            return el[`$v`][`pre_alert`].$invalid;
          } catch {
            return true;
          }
        },
      },
      {
        nameKey: "asn_information",
        valid: true,
        rules: (el) => {
          try {
            return el[`$v`][`asn_info`].$invalid;
          } catch {
            return true;
          }
        },
      },
      {
        nameKey: "quantity_information",
        valid: true,
        rules: (el) => {
          try {
            return el[`$v`][`qty_info`].$invalid;
          } catch {
            return true;
          }
        },
      },
    ],
  }),

  methods: {
    nextStep(type) {
      this.updateValidationStateOfSteps();
      if (type == "next") {
        if (this.categories[this.tab].valid && this.tab < 2) {
          this.tab += 1;
        }
      } else {
        if (this.tab > 0) {
          this.tab -= 1;
        }
      }
    },
    dateFormatter(date, hasSeconds = true) {
      try {
        if (!Object.prototype.toString.call(date) === "[object Date]") {
          return date;
        }
        if (!date) {
          return null;
        }
        let d = date;

        const formattedDate = [
          d.getFullYear(),
          d.getMonth() + 1 > 9 ? d.getMonth() + 1 : `0${d.getMonth() + 1}`,
          d.getDate() > 9 ? d.getDate() : `0${d.getDate()}`,
        ].join("-");

        const timeParts = [
          d.getHours() > 9 ? d.getHours() : `0${d.getHours()}`,
          d.getMinutes() > 9 ? d.getMinutes() : `0${d.getMinutes()}`,
        ];

        if (hasSeconds) {
          timeParts.push(
            d.getSeconds() > 9 ? d.getSeconds() : `0${d.getSeconds()}`
          );
        }

        const formattedTime = timeParts.join(":");
        return formattedDate + " " + formattedTime;
      } catch {
        return null;
      }
    },
    setSkus(item) {
      this.formData.skus = [...item];
    },
    updateValidationStateOfSteps() {
      // console.log(`working`);
      this.$v.$touch();
      this.categories.forEach((cat) => {
        cat.valid = !cat.rules(this);
        // console.log(cat);
      });
    },
    resetValidationStateOfSteps() {
      this.categories.forEach((cat) => {
        cat.valid = true;
      });
    },
    handleFormValidation(fieldName, vueObj, dataName = "formData") {
      const errors = [];
      if (!vueObj.$v[`${dataName}`][fieldName].$dirty) return errors;
      if ("required" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].required &&
          errors.push(vueObj.$t("validation_this_field_is_required"));
      }
      if ("maxValue" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].maxValue &&
          errors.push(vueObj.$t("validation_this_field_must_be_greater_than_0"));
      }
      if ("url" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].url &&
          errors.push(vueObj.$t("validation_this_url_is_invalid"));
      }
      if ("numeric" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].numeric &&
          errors.push(vueObj.$t("validation_this_must_be_a_number"));
      }
      if ("email" in vueObj.$v[`${dataName}`][fieldName]) {
        !vueObj.$v[`${dataName}`][fieldName].email &&
          errors.push(vueObj.$t("validation_this_email_address_is_invalid"));
      }
      return errors;
    },
    loadDataFromServer() {
      this.pageLoader(true);
      ApiService.post("/fulfillment/asn/create")
        .then((response) => {
          this.serverData = response.data;
          this.updateValidationStateOfSteps();
          this.resetEditForm();
        })
        .catch(() => {})
        .finally(() => {
          this.pageLoader(false);
        });
    },
    setQtys(items) {
      this.formData.additional_services_quantities = JSON.parse(
        JSON.stringify(items)
      );
    },
    toggleModal() {
      if (this.dialog) {
        this.serverData = null;
        this.resetCreateForm();
      } else {
        this.loadDataFromServer();
      }
      this.dialog = !this.dialog;
    },
    submitCreateForm() {
      this.updateValidationStateOfSteps();
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      // if (this.formData.skus.length === 0) {
      //   Swal.fire({
      //     title: "Warning",
      //     html: `<div>At least one sku is required!</div>`,
      //     icon: "warning",
      //     showConfirmButton: false,
      //     timer: 2500,
      //   });
      //   return;
      // }
      this.pageLoader(true);
      let data = JSON.parse(
        JSON.stringify({
          ...this.formData,
          ...this.pre_alert,
          ...this.asn_info,
          ...this.qty_info,
        })
      );
      data.additional_services_quantities =
        this.formData.additional_services_quantities;
      if (data.slot && data.asn_type_id === 1) {
        const lastDashIndex = this.pre_alert.slot.lastIndexOf("-");
        data.expected_arrival_time = this.pre_alert.slot.substring(
          0,
          lastDashIndex
        );
      } else if (
        data.asn_type_id !== 1 &&
        data.expected_arrival_time &&
        data.expected_arrival_time !== ""
      ) {
        data.expected_arrival_time = this.dateFormatter(
          this.pre_alert.expected_arrival_time,
          false
        );
      }

      if (data.actual_arrival_time && data.actual_arrival_time !== "") {
        data.actual_arrival_time = this.dateFormatter(
          this.pre_alert.actual_arrival_time,
          true
        );
      }

      ApiService.post("/fulfillment/asn/store", data)
        .then(() => {
          swalAdded();
          this.toggleModal();
          this.refresher();
        })
        .catch(() => {
          this.pageLoader(false);
        });
    },
    async resetCreateForm() {
      this.$v.$reset();
      this.formData = {
        skus: [],
        additional_services_quantities: [],
      };
      this.pre_alert = {
        customer_id: null,
        warehouse_id: null,
        carrier: null,
        supplier_reference_code: null,
        expected_arrival_time: null,
        slot: "",
        actual_arrival_time: null,
        po_number: null, // Purchase Order Number
        vehicle_number: null,
        vehicle_type_id: null,
        vehicle_temperature: null,
        invoice_number: null,
        number_of_boxes: null,
        note: null,
        reference_number: null,
      };
      this.asn_info = {
        asn_type_id: null,
        receiving_type: 1,
        release_status: false, // put on hold
        replacement_indicator: 0,
        original_order_awb: null,
        original_order_no: null,
        return_order_awb: null,
      };
      this.qty_info = {
        number_of_pallets_used: 0,
      };
      await this.resetValidationStateOfSteps();
      this.tab = 0;
    },
  },
  computed: {
    backState() {
      return this.tab !== 0;
    },
    nextState() {
      return this.tab !== 2;
    },
    isStepperReady() {
      try {
        return this.$v ? true : false;
      } catch (error) {
        return false;
      }
    },
    userId: function () {
      return this.isClient
        ? this.$store.getters.currentUser.data.customer_id
        : this.pre_alert.customer_id;
    },
    getCustomerData() {
      return this.pre_alert.customer_id ? this.pre_alert.customer_id : false;
    },
    isDataLoadedFromServer: function () {
      return !!this.serverData;
    },
    isClient() {
      return this.$store.getters.currentUser.data.is_client;
    },
    customer_idErrors() {
      return this.handleFormValidation("customer_id", this, "pre_alert");
    },

    asn_type_idErrors() {
      return this.handleFormValidation("asn_type_id", this, "asn_info");
    },
    original_order_awbErrors() {
      return this.handleFormValidation("original_order_awb", this, "asn_info");
    },
    original_order_noErrors() {
      return this.handleFormValidation("original_order_no", this, "asn_info");
    },
    return_order_awbErrors() {
      return this.handleFormValidation("return_order_awb", this, "asn_info");
    },
    warehouse_idErrors() {
      return this.handleFormValidation("warehouse_id", this, "pre_alert");
    },
    slotErrors() {
      return this.handleFormValidation("slot", this, "pre_alert");
    },
    number_of_pallets_usedErrors() {
      return this.handleFormValidation(
        "number_of_pallets_used",
        this,
        "qty_info"
      );
    },
    receivingTypes() {
      return [
        { text: this.$t("receiving_by_scanning_each"), index: 1 },
        { text: this.$t("receiving_bulk"), index: 2 },
        { text: this.$t("receiving_carton"), index: 3 },
      ];
    },
  },
  watch: {
    "asn_info.receiving_type": {
      handler(newValue, oldValue) {
        if (this.formData.skus.length == 0) {
          return;
        }
        if (this.updateFlag) {
          this.updateFlag = false;
          return;
        }
        Swal.fire({
          title: this.$t("are_you_sure"),
          text: this.$t("receiving_type_change_warning"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: this.$t("yes"),
          cancelButtonText: this.$t("no"),
        }).then((result) => {
          if (result.isConfirmed) {
            return;
          } else {
            this.updateFlag = true;
            this.asn_info.receiving_type = oldValue;
          }
        });
      },
      deep: true,
    },
  },
};
</script>
